import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  linkedSignal,
} from '@angular/core';
import {
  NgpAccordion,
  NgpAccordionContent,
  NgpAccordionItem,
  NgpAccordionTrigger,
} from 'ng-primitives/accordion';

/**
 * A single collapsible section.
 *
 * Each instance owns its own ngpAccordion, so two of them side by side
 * open and close independently rather than behaving as one exclusive
 * group. The accordion is collapsible so its lone panel can be closed
 * again — without that, a 'single' accordion refuses to close its only
 * open item.
 *
 * Intentionally unstyled. The primitive publishes open state as
 * data-open on the trigger and content, which is what the design system
 * should style against.
 */
@Component({
  selector: 'hh-accordion',
  standalone: true,
  imports: [NgpAccordion, NgpAccordionItem, NgpAccordionTrigger, NgpAccordionContent],
  template: `
    <div
      [class]="classes()"
      ngpAccordion
      ngpAccordionType="single"
      [ngpAccordionCollapsible]="true"
      [(ngpAccordionValue)]="open"
    >
      <div class="hh-accordion__item" ngpAccordionItem [ngpAccordionItemValue]="PANEL">
        <h3 class="hh-accordion__heading">
          <button class="hh-accordion__trigger" type="button" ngpAccordionTrigger>
            <span class="hh-accordion__caret" aria-hidden="true">&#9654;</span>
            <span class="hh-accordion__title">{{ header() }}</span>
          </button>
        </h3>
        <div class="hh-accordion__content" ngpAccordionContent>
          <ng-content />
        </div>
      </div>
    </div>
  `,
  styleUrl: './hh-accordion.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HhAccordionComponent {
  /** The lone panel's key; only its presence in `open` matters. */
  protected readonly PANEL = 'panel';

  readonly header = input('');

  /**
   * Seeds the open state. linkedSignal lets the accordion own it from
   * then on, so binding this does not fight the user's own toggling —
   * but a later change to the binding still re-seeds.
   */
  readonly expanded = input(false, { transform: booleanAttribute });

  /* Single-mode accordions hold a scalar value (or null), not an array. */
  protected readonly open = linkedSignal<string | null>(() =>
    this.expanded() ? this.PANEL : null,
  );

  /** 'light' for phone settings sheets, 'dark' for big-screen menus. */
  readonly variant = input<'light' | 'dark'>('light');

  protected readonly classes = computed(() => `hh-accordion hh-accordion--${this.variant()}`);
}
